import { useShallow } from 'zustand/react/shallow';
import { useMemo } from 'react';
import { type ComponentTFunction } from '@pkgs/components/providers/AppContextProvider';
import { type Language, type MarketCountryCode, type PlaceDetails } from '@ev-tech/search-modules-api';

import { useAppContext } from './useAppContext';
import { useAutoCompleteStore } from './stores/useAutoCompleteStore';
import { useSearchStore } from './stores/search/useSearchStore';
import { extractStateFromStore } from './stores/search/slices/utilSlice';
import { type SearchState } from './stores/search/SearchStoreProvider';
import { usePlaceDetails } from './queries/usePlaceDetails';
import { type PromptOptions, useAiSearch } from './queries/useAiSearch';
import {
  type FilterChip,
  getFeatureChips,
  getLocationChips,
  getMarketingTypeChip,
  getPreferenceChips,
  getPropertyTypeChips,
  getRangeChips,
} from '../utils/aiSearchChipUtils';

const prioritizeNonRemovableChips = (chips: FilterChip[]): FilterChip[] => {
  const { pinned = [], removable = [] } = Object.groupBy(chips, (chip) => (chip.isRemovable ? 'removable' : 'pinned'));

  return [...pinned, ...removable];
};

export const buildFilterChips = ({
  filters,
  t,
  language,
  promptOptions,
  placeDetails,
  marketCountryCode,
}: {
  filters: SearchState;
  t: ComponentTFunction;
  language: Language;
  promptOptions: PromptOptions;
  placeDetails: PlaceDetails[];
  marketCountryCode: MarketCountryCode;
}): FilterChip[] =>
  prioritizeNonRemovableChips([
    getMarketingTypeChip(filters.marketingType, t),
    ...getLocationChips({
      placeIds: filters.placeIds,
      placeName: filters.placeName,
      placeDetails,
      marketCountryCode,
    }),
    ...getPropertyTypeChips({ propertyTypeState: filters.propertyTypeState, objectTypeCategory: filters.objectTypeCategory, t }),
    ...getRangeChips({
      filters,
      t,
      language,
      promptOptions,
    }),
    ...getFeatureChips(filters.features, t),
    ...getPreferenceChips(filters.preferences, t),
  ]);

const useResolvedFilterChips = (filters: SearchState) => {
  const { t, countryCode, options } = useAppContext();
  const { promptOptions } = useAiSearch();
  const { placeDetailsList } = usePlaceDetails({ placeIds: filters.placeIds });

  return useMemo(() => {
    const chips = buildFilterChips({
      filters,
      t,
      language: options.language,
      promptOptions: promptOptions ?? options,
      placeDetails: placeDetailsList,
      marketCountryCode: countryCode,
    });

    const { filter = [], preference = [] } = Object.groupBy(chips, (chip) => chip.variant);

    return {
      chips,
      filterChips: filter,
      preferenceChips: preference,
    };
  }, [countryCode, filters, options, placeDetailsList, promptOptions, t]);
};

const useSearchState = () => useSearchStore(useShallow((state) => extractStateFromStore<SearchState>(state)));

export const useFilterChips = () => {
  const filters = useSearchState();

  return useResolvedFilterChips(filters);
};

export const useDraftFilterChips = () => {
  const searchFilters = useSearchState();
  const aiDraftState = useAutoCompleteStore((state) => state.aiDraftState);

  return useResolvedFilterChips(aiDraftState ?? searchFilters);
};
